/**
 * Game Comparison Zustand Store
 * 
 * Manages state for game comparison functionality including selected games,
 * comparison results, and UI state for the comparison interface.
 * 
 * Features:
 * - Game selection management
 * - Comparison results caching
 * - Comparison parameters (sorting, filtering)
 * - UI state for comparison modal/drawer
 * - Persistence for user preferences
 * 
 * @module TabPicker.ComparisonStore
 */

import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { JarFillImpact } from '../utils/comparisonCalculations';

/**
 * Game reference for comparison
 */
export interface GameRef {
  /** Game ID */
  id: number;
  /** Game display name */
  name: string;
  /** Whether game is selected for comparison */
  selected: boolean;
}

/**
 * Comparison sort options
 */
export type ComparisonSortBy = 'expectedValue' | 'winProbability' | 'holdPercentage' | 'name';
export type ComparisonSortOrder = 'asc' | 'desc';

/**
 * Claimed prizes tracking for a specific prize amount
 */
export interface ClaimedPrizes {
  [prizeAmount: string]: boolean[]; // Array of booleans for each individual prize
}

/**
 * Minor prize claims tracking for numeric counters
 */
export interface MinorPrizeClaims {
  [prizeAmount: string]: number; // Number of minor prizes claimed for this amount
}

/**
 * Game deal status for real-world odds adjustment
 */
export interface GameDealStatus {
  /** Game ID */
  gameId: number;
  /** Jar fill percentage (0-100%) */
  jarFillPercentage: number;
  /** Claimed prizes by amount - tracks which specific prizes have been won */
  claimedPrizes?: ClaimedPrizes;
  /** Minor prize claims by amount - tracks numeric counts for minor prizes */
  minorPrizeClaims?: MinorPrizeClaims;
  /** Optional detailed prize overrides */
  prizeOverrides?: Array<{
    prizeAmount: string;
    remainingQuantity: number;
  }>;
}

/**
 * Comparison configuration
 */
export interface ComparisonConfig {
  /** Field to sort comparison results by */
  sortBy: ComparisonSortBy;
  /** Sort order */
  sortOrder: ComparisonSortOrder;
  /** Whether to include detailed statistics */
  includeStatistics: boolean;
  /** Deal status for selected games */
  dealStatuses: GameDealStatus[];
  /** Whether to show original odds alongside current odds */
  showOriginalOdds: boolean;
}

/**
 * Comparison metrics for display
 */
export interface ComparisonMetrics {
  expectedValue: number;
  expectedValueRank: number;
  winProbability: number;
  winProbabilityRank: number;
  holdPercentage: number;
  holdPercentageRank: number;
  averagePrize: number;
  maxPrize: number;
  efficiency: number;
  efficiencyRank: number;
  riskScore: number;
  riskScoreRank: number;
  // NEW: Jar fill impact data for Phase 1A
  jarFillImpacts?: JarFillImpact[];
}

/**
 * Game with comparison data
 */
export interface GameComparison {
  id: number;
  userId: number;
  name: string;
  totalTickets: number;
  ticketPrice: string;
  totalPayout: string;
  holdPercentage: string;
  payoutStructure: {
    tickets: Array<{
      amount: string;
      quantity: number;
    }>;
  };
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  statistics?: {
    expectedValue: number;
    winProbability: number;
    averagePrize: number;
    maxPrize: number;
    profitMargin: number;
  };
  comparisonMetrics?: ComparisonMetrics;
}

/**
 * Comparison results
 */
export interface ComparisonResults {
  games: GameComparison[];
  summary: {
    totalGames: number;
    bestOverall: number;
    highestExpectedValue: number;
    highestWinProbability: number;
    lowestHoldPercentage: number;
    comparisonTimestamp: string;
  };
}

/**
 * UI State
 */
interface ComparisonUIState {
  /** Whether comparison panel is open */
  isOpen: boolean;
  /** Whether comparison is in progress */
  isLoading: boolean;
  /** Current comparison error if any */
  error: string | null;
  /** Whether game selection mode is active */
  selectionMode: boolean;
  /** Current toast message */
  toast: string | null;
}

/**
 * Comparison Store State
 */
interface ComparisonState extends ComparisonUIState {
  /** Available games for selection */
  availableGames: GameRef[];
  /** Selected games for comparison */
  selectedGameIds: number[];
  /** Current comparison configuration */
  config: ComparisonConfig;
  /** Latest comparison results */
  results: ComparisonResults | null;
  /** Timestamp of last successful comparison */
  lastComparisonTime: number | null;
}

/**
 * Comparison Store Actions
 */
interface ComparisonActions {
  // Game selection actions
  setAvailableGames: (games: GameRef[]) => void;
  toggleGameSelection: (gameId: number) => void;
  selectGame: (gameId: number) => void;
  deselectGame: (gameId: number) => void;
  clearSelection: () => void;
  selectAllGames: () => void;
  
  // Configuration actions
  setSortBy: (sortBy: ComparisonSortBy) => void;
  setSortOrder: (sortOrder: ComparisonSortOrder) => void;
  setIncludeStatistics: (include: boolean) => void;
  updateConfig: (config: Partial<ComparisonConfig>) => void;
  
  // Deal status actions
  setGameDealStatus: (gameId: number, jarFillPercentage: number) => void;
  removeGameDealStatus: (gameId: number) => void;
  clearAllDealStatuses: () => void;
  setShowOriginalOdds: (show: boolean) => void;
  
  // Prize claim actions
  togglePrizeClaim: (gameId: number, prizeAmount: string, prizeIndex: number) => void;
  updateMinorPrizeClaim: (gameId: number, prizeAmount: string, claimedCount: number) => void;
  
  // UI actions
  openComparison: () => void;
  closeComparison: () => void;
  toggleComparison: () => void;
  setSelectionMode: (active: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setToast: (message: string | null) => void;
  
  // Results actions
  setResults: (results: ComparisonResults | null) => void;
  clearResults: () => void;
  
  // Utility actions
  canCompare: () => boolean;
  getSelectedGames: () => GameRef[];
  resetStore: () => void;
}

/**
 * Combined Comparison Store Type
 */
export type ComparisonStore = ComparisonState & ComparisonActions;

/**
 * Initial comparison state
 */
const initialState: ComparisonState = {
  // Game data
  availableGames: [],
  selectedGameIds: [],
  
  // Configuration
  config: {
    sortBy: 'expectedValue',
    sortOrder: 'desc',
    includeStatistics: true,
    dealStatuses: [],
    showOriginalOdds: true,
  },
  
  // Results
  results: null,
  lastComparisonTime: null,
  
  // UI state
  isOpen: false,
  isLoading: false,
  error: null,
  selectionMode: false,
  toast: null,
};

/**
 * Game Comparison Zustand Store
 */
export const useComparisonStore = create<ComparisonStore>()(
  persist(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        // Game selection actions
        setAvailableGames: (games: GameRef[]) => {
          set((state) => {
            state.availableGames = games;
            // Remove selected games that are no longer available
            const availableIds = games.map(g => g.id);
            state.selectedGameIds = state.selectedGameIds.filter(id => availableIds.includes(id));
          });
        },

        toggleGameSelection: (gameId: number) => {
          set((state) => {
            const index = state.selectedGameIds.indexOf(gameId);
            if (index === -1) {
              // Don't exceed maximum of 10 games
              if (state.selectedGameIds.length < 10) {
                state.selectedGameIds.push(gameId);
              } else {
                state.toast = 'Maximum 10 games can be compared at once';
              }
            } else {
              state.selectedGameIds.splice(index, 1);
            }
            
            // Clear results when selection changes
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        selectGame: (gameId: number) => {
          set((state) => {
            if (!state.selectedGameIds.includes(gameId)) {
              if (state.selectedGameIds.length < 10) {
                state.selectedGameIds.push(gameId);
              } else {
                state.toast = 'Maximum 10 games can be compared at once';
              }
            }
            
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        deselectGame: (gameId: number) => {
          set((state) => {
            const index = state.selectedGameIds.indexOf(gameId);
            if (index !== -1) {
              state.selectedGameIds.splice(index, 1);
            }
            
            // Remove deal status for deselected game
            if (state.config.dealStatuses) {
              const dealStatusIndex = state.config.dealStatuses.findIndex(ds => ds?.gameId === gameId);
              if (dealStatusIndex >= 0) {
                state.config.dealStatuses.splice(dealStatusIndex, 1);
              }
            }
            
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        clearSelection: () => {
          set((state) => {
            state.selectedGameIds = [];
            state.config.dealStatuses = []; // Clear all deal statuses when clearing selection
            state.results = null;
            state.lastComparisonTime = null;
          });
        },

        selectAllGames: () => {
          set((state) => {
            // Select up to 10 games
            const availableIds = state.availableGames.map(g => g.id);
            state.selectedGameIds = availableIds.slice(0, 10);
            
            if (availableIds.length > 10) {
              state.toast = 'Only first 10 games selected (maximum limit)';
            }
            
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        // Configuration actions
        setSortBy: (sortBy: ComparisonSortBy) => {
          set((state) => {
            state.config.sortBy = sortBy;
            // Clear results to trigger re-comparison with new sort
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        setSortOrder: (sortOrder: ComparisonSortOrder) => {
          set((state) => {
            state.config.sortOrder = sortOrder;
            // Clear results to trigger re-comparison with new sort
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        setIncludeStatistics: (include: boolean) => {
          set((state) => {
            state.config.includeStatistics = include;
          });
        },

        updateConfig: (config: Partial<ComparisonConfig>) => {
          set((state) => {
            Object.assign(state.config, config);
            // Clear results if sort parameters changed
            if ('sortBy' in config || 'sortOrder' in config) {
              if (state.results) {
                state.results = null;
                state.lastComparisonTime = null;
              }
            }
          });
        },

        // Deal status actions
        setGameDealStatus: (gameId: number, jarFillPercentage: number) => {
          set((state) => {
            // Ensure jarFillPercentage is within valid range
            const clampedPercentage = Math.max(0, Math.min(100, jarFillPercentage));
            
            // Initialize dealStatuses if it doesn't exist
            if (!state.config.dealStatuses) {
              state.config.dealStatuses = [];
            }
            
            // Find existing deal status or create new one
            const existingIndex = state.config.dealStatuses.findIndex(ds => ds?.gameId === gameId);
            
            if (existingIndex >= 0) {
              // Update existing
              state.config.dealStatuses[existingIndex].jarFillPercentage = clampedPercentage;
            } else {
              // Add new deal status
              state.config.dealStatuses.push({
                gameId,
                jarFillPercentage: clampedPercentage,
              });
            }
            
            // Clear results to trigger re-calculation with new deal status
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        removeGameDealStatus: (gameId: number) => {
          set((state) => {
            if (!state.config.dealStatuses) {
              return; // Nothing to remove
            }
            const index = state.config.dealStatuses.findIndex(ds => ds?.gameId === gameId);
            if (index >= 0) {
              state.config.dealStatuses.splice(index, 1);
              
              // Clear results to trigger re-calculation
              if (state.results) {
                state.results = null;
                state.lastComparisonTime = null;
              }
            }
          });
        },

        clearAllDealStatuses: () => {
          set((state) => {
            state.config.dealStatuses = [];
            
            // Clear results to trigger re-calculation
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        setShowOriginalOdds: (show: boolean) => {
          set((state) => {
            state.config.showOriginalOdds = show;
          });
        },
        
        // Prize claim actions
        togglePrizeClaim: (gameId: number, prizeAmount: string, prizeIndex: number) => {
          set((state) => {
            // Initialize dealStatuses if it doesn't exist
            if (!state.config.dealStatuses) {
              state.config.dealStatuses = [];
            }
            
            // Find or create deal status for this game
            let dealStatus = state.config.dealStatuses.find(ds => ds?.gameId === gameId);
            if (!dealStatus) {
              dealStatus = {
                gameId,
                jarFillPercentage: 100, // Default to full jar
                claimedPrizes: {}
              };
              state.config.dealStatuses.push(dealStatus);
            }
            
            // Initialize claimed prizes if not exists
            if (!dealStatus.claimedPrizes) {
              dealStatus.claimedPrizes = {};
            }
            
            // Initialize prize amount array if not exists
            if (!dealStatus.claimedPrizes[prizeAmount]) {
              // We'll need to know the total quantity from the game data
              // For now, initialize an empty array that will be populated properly in the UI
              dealStatus.claimedPrizes[prizeAmount] = [];
            }
            
            // Ensure array is large enough for this index
            while (dealStatus.claimedPrizes[prizeAmount].length <= prizeIndex) {
              dealStatus.claimedPrizes[prizeAmount].push(false);
            }
            
            // Toggle the claim status
            dealStatus.claimedPrizes[prizeAmount][prizeIndex] = !dealStatus.claimedPrizes[prizeAmount][prizeIndex];
            
            // Clear results to trigger re-calculation
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        updateMinorPrizeClaim: (gameId: number, prizeAmount: string, claimedCount: number) => {
          set((state) => {
            // Initialize dealStatuses if it doesn't exist
            if (!state.config.dealStatuses) {
              state.config.dealStatuses = [];
            }
            
            // Find or create deal status for this game
            let dealStatus = state.config.dealStatuses.find(ds => ds?.gameId === gameId);
            if (!dealStatus) {
              dealStatus = {
                gameId,
                jarFillPercentage: 100, // Default to full jar
                minorPrizeClaims: {}
              };
              state.config.dealStatuses.push(dealStatus);
            }
            
            // Initialize minor prize claims if not exists
            if (!dealStatus.minorPrizeClaims) {
              dealStatus.minorPrizeClaims = {};
            }
            
            // Update the claimed count for this prize amount
            dealStatus.minorPrizeClaims[prizeAmount] = Math.max(0, claimedCount);
            
            // Clear results to trigger re-calculation
            if (state.results) {
              state.results = null;
              state.lastComparisonTime = null;
            }
          });
        },

        // UI actions
        openComparison: () => {
          set((state) => {
            state.isOpen = true;
          });
        },

        closeComparison: () => {
          set((state) => {
            state.isOpen = false;
            state.selectionMode = false;
            state.error = null;
          });
        },

        toggleComparison: () => {
          set((state) => {
            state.isOpen = !state.isOpen;
            if (!state.isOpen) {
              state.selectionMode = false;
              state.error = null;
            }
          });
        },

        setSelectionMode: (active: boolean) => {
          set((state) => {
            state.selectionMode = active;
          });
        },

        setLoading: (loading: boolean) => {
          set((state) => {
            state.isLoading = loading;
            if (loading) {
              state.error = null;
            }
          });
        },

        setError: (error: string | null) => {
          set((state) => {
            state.error = error;
            state.isLoading = false;
          });
        },

        setToast: (message: string | null) => {
          set((state) => {
            state.toast = message;
          });
        },

        // Results actions
        setResults: (results: ComparisonResults | null) => {
          set((state) => {
            state.results = results;
            state.lastComparisonTime = results ? Date.now() : null;
            state.isLoading = false;
            state.error = null;
          });
        },

        clearResults: () => {
          set((state) => {
            state.results = null;
            state.lastComparisonTime = null;
          });
        },

        // Utility actions
        canCompare: () => {
          const state = get();
          return state.selectedGameIds.length >= 2 && !state.isLoading;
        },

        getSelectedGames: () => {
          const state = get();
          return state.availableGames.filter(game => 
            state.selectedGameIds.includes(game.id)
          );
        },

        resetStore: () => {
          set((state) => {
            Object.assign(state, initialState);
          });
        },
      }))
    ),
    {
      name: 'comparison-store',
      // Persist configuration and selection preferences
      partialize: (state) => ({
        selectedGameIds: state.selectedGameIds,
        config: state.config,
      }),
    }
  )
);

/**
 * Comparison Store Selectors
 */
export const comparisonSelectors = {
  /** Select available games */
  availableGames: (state: ComparisonStore) => state.availableGames,
  /** Select selected game IDs */
  selectedGameIds: (state: ComparisonStore) => state.selectedGameIds,
  /** Select selected games with details */
  selectedGames: (state: ComparisonStore) => state.getSelectedGames(),
  /** Select comparison configuration */
  config: (state: ComparisonStore) => state.config,
  /** Select comparison results */
  results: (state: ComparisonStore) => state.results,
  /** Select UI state */
  uiState: (state: ComparisonStore) => ({
    isOpen: state.isOpen,
    isLoading: state.isLoading,
    error: state.error,
    selectionMode: state.selectionMode,
    toast: state.toast,
  }),
  /** Select if comparison can be performed */
  canCompare: (state: ComparisonStore) => state.canCompare(),
};

/**
 * Convenience hooks for common comparison store operations
 */

/** Hook to get selected games */
export const useSelectedGames = () => useComparisonStore(comparisonSelectors.selectedGames);

/** Hook to get comparison configuration */
export const useComparisonConfig = () => useComparisonStore(comparisonSelectors.config);

/** Hook to get comparison results */
export const useComparisonResults = () => useComparisonStore(comparisonSelectors.results);

/** Hook to get UI state */
export const useComparisonUI = () => useComparisonStore(comparisonSelectors.uiState);

/** Hook to check if comparison is possible */
export const useCanCompare = () => useComparisonStore(comparisonSelectors.canCompare);